import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { keys } from "../../../api/queryKeys.ts";
import { api, getErrorMessage, type MicroApp } from "../../../api/client.ts";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import CopyButton from "../../../components/CopyButton.tsx";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";

/**
 * Share one micro app to a wall TV: a public `dash.sxq.no/<code>` link that
 * needs no login. The TV renders as the app's tenant, not as whoever turned
 * sharing on. Regenerating the code or stopping sharing kills the old link.
 */
function MicroAppTvShareDialog({ app, onClose }: { app: MicroApp | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Both kill the link the TV has now, so they ask first.
  const [confirming, setConfirming] = useState<"stop" | "regenerate" | null>(null);

  const { data: share, isLoading } = useQuery({
    queryKey: keys.microAppTvShare.byApp(app?.id),
    queryFn: () => api.getMicroAppTvShare(app!.id),
    enabled: !!app,
  });

  const onDone = () => {
    setError(null);
    setConfirming(null);
    queryClient.invalidateQueries({ queryKey: keys.microAppTvShare.byApp(app?.id) });
    queryClient.invalidateQueries({ queryKey: keys.microApp.all });
  };

  const enable = useMutation({
    mutationFn: () => api.enableMicroAppTvShare(app!.id),
    onSuccess: onDone,
    // The error shows in this dialog, so close the confirm that would hide it.
    onError: (e) => {
      setError(getErrorMessage(e));
      setConfirming(null);
    },
    meta: { suppressErrorToast: true },
  });
  const disable = useMutation({
    mutationFn: () => api.disableMicroAppTvShare(app!.id),
    onSuccess: onDone,
    // The error shows in this dialog, so close the confirm that would hide it.
    onError: (e) => {
      setError(getErrorMessage(e));
      setConfirming(null);
    },
    meta: { suppressErrorToast: true },
  });
  const busy = enable.isPending || disable.isPending;

  return (
    <Dialog open={!!app} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share to TV: {app?.title}</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {app?.tenant_id === null ? (
          <Alert severity="info">
            Global apps can’t be shared to a TV — only an app that belongs to a tenant can.
          </Alert>
        ) : isLoading ? (
          <CenteredSpinner py={3} size={24} />
        ) : share?.code && share.url ? (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Open this link on the TV, or go to{" "}
              <strong>{new URL(share.url).host}</strong> and enter the code. The page refreshes
              its data on its own, picks up new versions, and reloads every hour.
            </Typography>
            <Box sx={{ textAlign: "center", py: 1 }}>
              <Typography
                sx={{ fontSize: 48, fontWeight: 600, letterSpacing: "0.3em", fontFamily: "monospace" }}
              >
                {share.code}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                <Typography component="code" variant="body2">
                  {share.url}
                </Typography>
                <CopyButton text={share.url} label="Copy link" successMessage="Link copied" />
              </Stack>
            </Box>
            <Alert severity="warning" variant="outlined">
              Anyone with this code can see the dashboard, without logging in, with the data of
              the app’s tenant. Regenerate the code if it leaks.
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2">
              Sharing creates a link with a short numeric code that shows this dashboard full-screen,
              without a login. Use it for a TV in the office.
            </Typography>
            <Alert severity="warning" variant="outlined">
              Anyone with the code sees the dashboard with the data of the app’s tenant —
              regardless of who is allowed to open it in Hive.
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {share?.code ? (
          <>
            <Button color="error" onClick={() => setConfirming("stop")} disabled={busy}>
              Stop sharing
            </Button>
            <Button variant="outlined" onClick={() => setConfirming("regenerate")} disabled={busy}>
              New code
            </Button>
          </>
        ) : app?.tenant_id ? (
          <Button
            variant="contained"
            onClick={() => enable.mutate()}
            loading={enable.isPending}
            disabled={busy || isLoading}
          >
            Share to TV
          </Button>
        ) : null}
      </DialogActions>
      <ConfirmDialog
        open={confirming !== null}
        title={confirming === "stop" ? "Stop sharing to TV" : "Make a new code"}
        confirmLabel={confirming === "stop" ? "Stop sharing" : "New code"}
        color={confirming === "stop" ? "error" : "warning"}
        pending={busy}
        onConfirm={() => (confirming === "stop" ? disable.mutate() : enable.mutate())}
        onClose={() => setConfirming(null)}
      >
        {confirming === "stop"
          ? "The TV link stops working at once."
          : "The current link and code stop working at once; open the new link on the TV."}
      </ConfirmDialog>
    </Dialog>
  );
}

export default MicroAppTvShareDialog;
